import { Event } from "../../../../Global/Base/Events";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { IItemRightsCollection } from "../../../../Global/Base/Rights/RightTypes";

/**
 * Tento soubor obsahuje typy kompatibilní se strict režimem kompilace. Všechny typy zde pocházejí
 * z {@link ControlInterfaces} a jsou tam kvůli zpětné kompatibilitě reexportovány. Ve chvíli, kdy
 * bude ControlInterfaces obsahovat jen striktní typy, tak se může sloučit s tímto souborem.
 */

/**
 * Interface pro komponenty, u kterých je možné měnit viditelnost.
 */
export interface IVisible {
    visibilityChanged: Event<IControl, boolean>;
    visible: boolean;
}

export class IVisible {
    private static _properties: (keyof IVisible)[] = ["visibilityChanged", "visible"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Interface pro komponenty, které umožňují lokalizaci jazyka.
 */
export interface ILocalizable {
    locKey: string;
}

export class ILocalizable {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "locKey" as keyof ILocalizable);
    }
}

/**
 * Rozhraní umožňuje řídit vizuální stav zašifrovanosti komponenty
 * @includeToDoc
 */
export interface IHasEncryptionState {
    /**
     * Zda se má komponenta zobrazovat jako zašifrovaná (zobrazující zašifrovanou hodnotu)
     */
    isVisuallyEncrypted: boolean;
}

export class IHasEncryptionState {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "isVisuallyEncrypted" as keyof IHasEncryptionState);
    }
}

/**
 * Interface pro komponenty, které obsahují lokalizační kultůru pro nastavení formátování
 */
export interface IHasCulture {
    /** Událost vyvolaná při změné kultůry */
    cultureChanged: Event<IControl, string>;
    culture: string;
}

export class IHasCulture {
    private static _properties: (keyof IHasCulture)[] = ["cultureChanged", "culture"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Interface pro komponenty, které můžou načíst aplikační data a zobrazit je.
 */
export interface ILoadableValue {
    canValueLoadChanged: Event<IControl, boolean>;
    canValueLoad: boolean;

    /**
     * Metoda vyhodnotí zda se má vyvolat událost canValueLoadChange a případně událost vyvolá.
     */
    tryTriggerCanValueLoadChange(sender: IControl, value: boolean): boolean;
}

export class ILoadableValue {
    private static _properties: (keyof ILoadableValue)[] = ["canValueLoadChanged", "canValueLoad"];
    private static _methods: (keyof ILoadableValue)[] = ["tryTriggerCanValueLoadChange"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface IEnable {
    enabled: boolean;
}

export class IEnable {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "enabled" as keyof IEnable);
    }
}

/**
 * Komponenta, která potenciálně může obsahovat definici práv (z XML definic).
 * - Jde o všechny controls implementující {@link IGenericControl}.
 * - A navíc i o {@link FileColumn}, která drží definice práv pro potřeby {@link FileEdit} a {@link FileCellElement}.
 *
 * Nad tímto rozhraním pracuje {@link ComponentRightsExtractor.loadRightsForTypes}.
 */
export interface IWithOriginalRightsDefinition {
    /**
     * Definice práv k danému základnímu control, přesně tak, jak je to uvedeno v XML definici.
     * Slouží primárně jako podklad pro `ComponentRightsExtractor.loadRightsForTypes`.
     *
     * Nastavuje se typicky při registraci control.
     */
    readonly originalRightsDefinition: IItemRightsCollection;
}

export class IWithOriginalRightsDefinition {
    private static _properties: [keyof IWithOriginalRightsDefinition] = ["originalRightsDefinition"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value));
    }
}

/**
 * Základní control, který nemá nic víc než HTMLElement, definici práv a destruktor
 * @includeToDoc
 */
export interface IGenericControl extends IDisposable, IWithOriginalRightsDefinition {
    element: HTMLElement;
}

export class IGenericControl {
    private static _properties = ["element"];
    private static _interfaces = [IDisposable, IWithOriginalRightsDefinition];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value)) &&
            this._interfaces.every((_interface) => instance instanceof _interface);
    }
}

/**
 * Rozhraní pro základní needitační komponenty.
 * @includeToDoc
 */
export interface IControl extends IGenericControl, ILocalizable, IHasEncryptionState, IVisible, ILoadableValue, IEnable {
    componentName: string;
}

export class IControl {
    private static _properties: string[] = ["componentName"];
    private static _interfaces = [IGenericControl, ILocalizable, IHasEncryptionState, IVisible, ILoadableValue, IEnable];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value)) &&
            this._interfaces.every((_interface) => instance instanceof _interface);
    }
}
